import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
});

const base64ToFile = async (base64, fileName, type) => {
    const res = await fetch(base64);
    const blob = await res.blob();
    return new File([blob], fileName, { type });
};

const useQuestionDetailStore = create(
    persist(
        (set, get) => ({
            imageUrls: [],
            htmlUrls: [],
            assetsLoading: false,
            assetsError: null,
            questionText: '',
            answerText: '',
            isActive: false,
            monthYearInput: '',
            newImages: [],
            newHtmlFile: null,
            persistedNewImages: [],
            persistedNewHtmlFile: null,
            deletedImages: [],
            deletedHtmlFiles: [],

            parsedData: {},
            questionId: null,
            questionUuid: null,
            moduleTopicUuid: null,
            moduleTopicId: null,
            saveLoading: false,
            saveError: null,
            saveSuccess: null,
            showHtmlModal: false,
            htmlViewContent: '',
            zoomImage: null,

            setImageUrls: (urls) => set({ imageUrls: urls }),
            setHtmlUrls: (urls) => set({ htmlUrls: urls }),
            setAssetsLoading: (loading) => set({ assetsLoading: loading }),
            setAssetsError: (error) => set({ assetsError: error }),
            setQuestionText: (text) => set({ questionText: text }),
            setAnswerText: (text) => set({ answerText: text }),
            setIsActive: (isActive) => set({ isActive }),
            setMonthYearInput: (input) => set({ monthYearInput: input }),
            setZoomImage: (url) => set({ zoomImage: url }),

            setNewImages: async (images) => {
                const persisted = await Promise.all(images.map(async (file) => ({
                    name: file.name,
                    type: file.type,
                    data: await fileToBase64(file)
                })));
                set({ newImages: images, persistedNewImages: persisted });
            },

            addNewImages: async (images) => {
                const newPersisted = await Promise.all(images.map(async (file) => ({
                    name: file.name,
                    type: file.type,
                    data: await fileToBase64(file)
                })));
                set((state) => ({
                    newImages: [...state.newImages, ...images],
                    persistedNewImages: [...state.persistedNewImages, ...newPersisted]
                }));
            },

            removeNewImage: (index) => set((state) => ({
                newImages: state.newImages.filter((_, i) => i !== index),
                persistedNewImages: state.persistedNewImages.filter((_, i) => i !== index)
            })),

            setDeletedImages: (images) => set({ deletedImages: images }),
            addDeletedImage: (imageName) => set((state) => ({ deletedImages: [...state.deletedImages, imageName] })),

            setNewHtmlFile: async (file) => {
                if (!file) {
                    set({ newHtmlFile: null, persistedNewHtmlFile: null });
                    return;
                }
                const base64 = await fileToBase64(file);
                set({
                    newHtmlFile: file,
                    persistedNewHtmlFile: { name: file.name, type: file.type, data: base64 }
                });
            },

            setDeletedHtmlFiles: (files) => set({ deletedHtmlFiles: files }),
            addDeletedHtmlFile: (fileName) => set((state) => ({ deletedHtmlFiles: [...state.deletedHtmlFiles, fileName] })),
            setSaveLoading: (loading) => set({ saveLoading: loading }),
            setSaveError: (error) => set({ saveError: error }),
            setSaveSuccess: (success) => set({ saveSuccess: success }),
            setShowHtmlModal: (show) => set({ showHtmlModal: show }),
            setHtmlViewContent: (content) => set({ htmlViewContent: content }),
            initQuestion: (question) => {
                let parsedData = {};
                try {
                    parsedData = question?.data ? JSON.parse(question.data) || {} : {};
                } catch {
                    parsedData = {};
                }

                const currentId = get().questionId;
                if (currentId === question?.id) {
                    return;
                }

                set({
                    questionId: question?.id,
                    questionUuid: question?.uuid,
                    moduleTopicUuid: question?.moduleTopicUuid || question?.moduleTopic?.uuid || null,
                    questionText: question?.questionText || question?.text || '',
                    answerText: question?.answerText || question?.answer || '',
                    isActive: !!question?.isActive,
                    monthYearInput: question?.monthYears?.map(
                        (item) => `${item?.month || ''} ${item?.year || ''}`.trim()
                    ).join(', ') || '',
                    parsedData,
                    newImages: [],
                    persistedNewImages: [],
                    deletedImages: [],
                    newHtmlFile: null,
                    persistedNewHtmlFile: null,
                    deletedHtmlFiles: [],
                    imageUrls: [],
                    htmlUrls: [],
                    assetsError: null,
                    saveError: null,
                    saveSuccess: null,
                });
            },

            restoreFiles: async () => {
                const { newImages, persistedNewImages, newHtmlFile, persistedNewHtmlFile } = get();
                if (newImages.length === 0 && persistedNewImages.length > 0) {
                    const restoredImages = await Promise.all(persistedNewImages.map(p => base64ToFile(p.data, p.name, p.type)));
                    set({ newImages: restoredImages });
                }

                if (!newHtmlFile && persistedNewHtmlFile) {
                    const restoredHtml = await base64ToFile(persistedNewHtmlFile.data, persistedNewHtmlFile.name, persistedNewHtmlFile.type);
                    set({ newHtmlFile: restoredHtml });
                }
            },

            resetStore: () => set({
                imageUrls: [], htmlUrls: [], assetsLoading: false, assetsError: null,
                questionText: '', answerText: '', isActive: false, monthYearInput: '',
                newImages: [], persistedNewImages: [], deletedImages: [],
                newHtmlFile: null, persistedNewHtmlFile: null, deletedHtmlFiles: [],
                parsedData: {},
                questionId: null, questionUuid: null, moduleTopicUuid: null,
                saveLoading: false, saveError: null, saveSuccess: null,
                showHtmlModal: false, htmlViewContent: '', zoomImage: null
            })
        }),
        {
            name: 'question-detail-storage',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                questionText: state.questionText,
                answerText: state.answerText,
                isActive: state.isActive,
                monthYearInput: state.monthYearInput,
                questionId: state.questionId,
                questionUuid: state.questionUuid,
                moduleTopicUuid: state.moduleTopicUuid,
                deletedImages: state.deletedImages,
                deletedHtmlFiles: state.deletedHtmlFiles,

                imageUrls: state.imageUrls,
                htmlUrls: state.htmlUrls,
                persistedNewImages: state.persistedNewImages,
                persistedNewHtmlFile: state.persistedNewHtmlFile
            })
        }
    )
);

export default useQuestionDetailStore;
